import { Dumbbell, Flame, Heart, ChevronDown, Trash2, GripVertical, Plus, ChevronRight } from "lucide-react";
import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  { id: "warmup", labelDE: "Aufwärmen", icon: Flame, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  { id: "main", labelDE: "Haupttraining", icon: Dumbbell, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700" },
  { id: "cooldown", labelDE: "Dehnung", icon: Heart, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
];

export function PlanOverview({ days, setDays, getMuscleLabel, onClose, onAddExercise, setActiveDay }) {
  const [expandedDay, setExpandedDay] = useState(null);
  const [dragState, setDragState] = useState({ dayIdx: null, exIdx: null, overIdx: null });

  const updateExercise = useCallback((dayIdx, exIdx, field, value) => {
    setDays(prev => prev.map((d, di) => di === dayIdx
      ? { ...d, exercises: d.exercises.map((ex, ei) => ei === exIdx ? { ...ex, [field]: value } : ex) }
      : d
    ));
  }, [setDays]);

  const removeExercise = useCallback((dayIdx, exIdx) => {
    setDays(prev => prev.map((d, di) => di === dayIdx
      ? { ...d, exercises: d.exercises.filter((_, ei) => ei !== exIdx) }
      : d
    ));
  }, [setDays]);

  const cycleSectionForExercise = useCallback((dayIdx, exIdx) => {
    const order = ["warmup", "main", "cooldown"];
    setDays(prev => prev.map((d, di) => di === dayIdx
      ? { ...d, exercises: d.exercises.map((ex, ei) => {
          if (ei !== exIdx) return ex;
          const current = ex.section || "main";
          return { ...ex, section: order[(order.indexOf(current) + 1) % order.length] };
        })}
      : d
    ));
  }, [setDays]);

  const handleDragStart = (dayIdx, exIdx) => setDragState({ dayIdx, exIdx, overIdx: null });
  const handleDragOver = (e, overIdx) => { e.preventDefault(); setDragState(prev => ({ ...prev, overIdx })); };
  const handleDrop = (dayIdx, dropIdx) => {
    const { dayIdx: srcDay, exIdx: srcIdx } = dragState;
    if (srcDay !== dayIdx || srcIdx === null || srcIdx === dropIdx) { setDragState({ dayIdx: null, exIdx: null, overIdx: null }); return; }
    setDays(prev => prev.map((d, di) => {
      if (di !== dayIdx) return d;
      const items = [...d.exercises];
      const [moved] = items.splice(srcIdx, 1);
      items.splice(dropIdx, 0, moved);
      return { ...d, exercises: items };
    }));
    setDragState({ dayIdx: null, exIdx: null, overIdx: null });
  };

  const handleAddToDay = (dayIdx) => {
    setActiveDay(dayIdx);
    onAddExercise();
  };

  return (
    <div className="space-y-3" data-testid="plan-overview">
      <div className="flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-bold text-slate-900">Planübersicht</h2>
        <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100 transition-colors" data-testid="close-overview">Zurück zum Editor</button>
      </div>

      {days.map((day, dayIdx) => {
        const exercises = day.exercises || [];
        const isOpen = expandedDay === null || expandedDay === dayIdx;

        return (
          <div key={dayIdx} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`overview-day-${dayIdx}`}>
            <button
              onClick={() => setExpandedDay(prev => prev === dayIdx ? null : dayIdx)}
              className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">{dayIdx + 1}</span>
                <div className="text-left">
                  <p className="text-xs sm:text-sm font-bold text-slate-900">{day.name}</p>
                  <p className="text-[10px] sm:text-xs text-slate-400">{exercises.length} Übungen</p>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? '' : '-rotate-90'}`} />
            </button>

            {isOpen && (
              <div className="p-2 sm:p-3">
                {SECTIONS.map(sec => {
                  const sectionExercises = exercises
                    .map((ex, i) => ({ ex, globalIdx: i }))
                    .filter(({ ex }) => (ex.section || "main") === sec.id);
                  if (sectionExercises.length === 0) return null;
                  const SIcon = sec.icon;

                  return (
                    <div key={sec.id} className="mb-3 last:mb-0">
                      <div className={`flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded ${sec.bg}`}>
                        <SIcon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${sec.text}`} />
                        <span className={`text-[10px] sm:text-xs font-bold ${sec.text} uppercase tracking-wider`}>{sec.labelDE}</span>
                        <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full ${sec.badge} font-medium ml-1`}>{sectionExercises.length}</span>
                      </div>

                      {/* Compact table-like layout */}
                      <div className="space-y-1">
                        {sectionExercises.map(({ ex, globalIdx }) => (
                          <div key={globalIdx}
                            draggable
                            onDragStart={() => handleDragStart(dayIdx, globalIdx)}
                            onDragOver={(e) => handleDragOver(e, globalIdx)}
                            onDrop={() => handleDrop(dayIdx, globalIdx)}
                            onDragEnd={() => setDragState({ dayIdx: null, exIdx: null, overIdx: null })}
                            className={`flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-1.5 sm:py-2 rounded-lg border transition-all ${
                              dragState.dayIdx === dayIdx && dragState.overIdx === globalIdx ? 'border-blue-400 bg-blue-50/50'
                              : dragState.dayIdx === dayIdx && dragState.exIdx === globalIdx ? 'opacity-40 border-slate-200'
                              : 'border-slate-100 hover:bg-slate-50/80'
                            }`}
                            data-testid={`overview-ex-${dayIdx}-${globalIdx}`}>

                            {/* Drag handle */}
                            <div className="cursor-grab active:cursor-grabbing select-none flex-shrink-0 hidden sm:block">
                              <GripVertical className="w-3 h-3 text-slate-300" />
                            </div>

                            {/* Section cycle */}
                            <button onClick={(e) => { e.stopPropagation(); cycleSectionForExercise(dayIdx, globalIdx); }}
                              className={`w-5 h-5 rounded-full ${sec.badge} flex items-center justify-center flex-shrink-0`} title="Sektion wechseln">
                              <ChevronRight className="w-2.5 h-2.5" />
                            </button>

                            {/* Image */}
                            {ex.image && <img src={ex.image} alt={ex.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0" loading="lazy" />}

                            {/* Name + muscle */}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-semibold text-slate-900 truncate leading-tight">{ex.name}</p>
                              <span className="text-[9px] sm:text-[10px] px-1 py-0.5 bg-slate-100 rounded text-slate-500 font-medium">{getMuscleLabel(ex.muscle_group)}</span>
                            </div>

                            {/* Inline inputs */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <div className="text-center">
                                <p className="text-[8px] sm:text-[9px] text-pink-500 font-semibold leading-none mb-0.5">Sätze</p>
                                <Input value={ex.sets || "3"} onChange={e => updateExercise(dayIdx, globalIdx, "sets", e.target.value)}
                                  className="h-6 w-9 sm:w-11 text-[10px] sm:text-[11px] text-center px-0" data-testid={`ov-sets-${dayIdx}-${globalIdx}`} />
                              </div>
                              <div className="text-center">
                                <p className="text-[8px] sm:text-[9px] text-violet-500 font-semibold leading-none mb-0.5">Wdh.</p>
                                <Input value={ex.reps || "12"} onChange={e => updateExercise(dayIdx, globalIdx, "reps", e.target.value)}
                                  className="h-6 w-9 sm:w-11 text-[10px] sm:text-[11px] text-center px-0" data-testid={`ov-reps-${dayIdx}-${globalIdx}`} />
                              </div>
                              <div className="text-center">
                                <p className="text-[8px] sm:text-[9px] text-amber-500 font-semibold leading-none mb-0.5">kg</p>
                                <Input value={ex.weight_kg || "0"} onChange={e => updateExercise(dayIdx, globalIdx, "weight_kg", e.target.value)}
                                  className="h-6 w-9 sm:w-11 text-[10px] sm:text-[11px] text-center px-0" data-testid={`ov-weight-${dayIdx}-${globalIdx}`} />
                              </div>
                            </div>

                            {/* Delete */}
                            <button onClick={() => removeExercise(dayIdx, globalIdx)}
                              className="p-1 rounded hover:bg-red-50 text-red-300 hover:text-red-500 flex-shrink-0 transition-colors" title="Entfernen"
                              data-testid={`ov-remove-${dayIdx}-${globalIdx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <Button size="sm" variant="outline" className="w-full mt-2 h-8 text-xs" onClick={() => handleAddToDay(dayIdx)} data-testid={`ov-add-exercise-${dayIdx}`}>
                  <Plus className="w-3 h-3 mr-1" /> Übung hinzufügen
                </Button>

                {exercises.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Keine Übungen</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
